import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from './components/Badge';
import { AssignModal } from './components/AssignModal';
import { CancelModal } from './components/CancelModal';
import { AuditLog } from './components/AuditLog';
import { CalibrationReport } from './components/CalibrationReport';
import { EditReservationModal } from './components/EditReservationModal';
import { EquipmentModal } from './components/EquipmentModal';
import { NewReservationForm } from './components/NewReservationForm';
import { ResourceModal } from './components/ResourceModal';
import { ApiError, NotAuthenticatedError, api, login, logout } from './lib/api';
import {
  AVAILABILITY_COLORS,
  CAL_COLORS,
  RESERVATION_COLORS,
  TYPE_COLORS,
  addDays,
  calibrationStatus,
  initials,
  parseIsoDate,
  todayIso,
} from './lib/format';
import type {
  Availability,
  CurrentUser,
  EnrichedResource,
  EquipmentInput,
  NewReservation,
  Reservation,
  ReservationEdit,
  Resource,
} from './lib/types';

type AuthState = 'loading' | 'loggedout' | 'loggedin';
type View = 'resources' | 'matrix' | 'reservations' | 'reports' | 'audit';

const VIEWS: [View, string][] = [
  ['resources', 'All Resources'],
  ['matrix', 'Availability'],
  ['reservations', 'Reservations'],
  ['reports', 'Reports'],
];

/** Appended only for admins — the API refuses everyone else anyway. */
const ADMIN_VIEWS: [View, string][] = [['audit', 'Audit log']];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

export default function App() {
  const today = useMemo(todayIso, []);

  const [authState, setAuthState] = useState<AuthState>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>('resources');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Resources');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [officeFilter, setOfficeFilter] = useState('All Offices');
  const [reservationSearch, setReservationSearch] = useState('');
  const [reservationStatusFilter, setReservationStatusFilter] = useState('All');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [selected, setSelected] = useState<EnrichedResource | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Reservation | null>(null);
  const [assignTarget, setAssignTarget] = useState<Reservation | null>(null);
  const [editingReservation, setEditingReservation] =
    useState<Reservation | null>(null);
  const [showNew, setShowNew] = useState(false);

  // Catalogue editing (admin only). `editing` holds the resource being
  // edited, or null while adding; `catalogueOpen` is what distinguishes
  // "adding" from "closed".
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);

  // ── Bootstrap ───────────────────────────────────────────────
  // /api/me is the session probe. SWA owns the cookie, so there is no
  // client-side token to inspect — asking the API is the only truth.
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        setAuthState('loggedin');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          setAuthState('loggedout');
        } else {
          setError(errorMessage(err));
          setAuthState('loggedout');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(async () => {
    setDataLoading(true);
    setError(null);
    try {
      const [resourceRows, reservationRows] = await Promise.all([
        api.listResources(includeRetired),
        api.listReservations(),
      ]);
      setResources(resourceRows);
      setReservations(reservationRows);
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        setAuthState('loggedout');
      } else {
        setError('Failed to load data: ' + errorMessage(err));
      }
    }
    setDataLoading(false);
  }, [includeRetired]);

  useEffect(() => {
    if (authState === 'loggedin') void loadData();
  }, [authState, loadData]);

  // ── Mutations ───────────────────────────────────────────────
  async function addReservation(input: NewReservation) {
    setSaving(true);
    setError(null);
    try {
      const created = await api.createReservation(input);
      setReservations((prev) => [created, ...prev]);
      setShowNew(false);
      setView('reservations');
    } catch (err) {
      setError('Failed to save: ' + errorMessage(err));
    }
    setSaving(false);
  }

  async function cancelReservation(
    target: Reservation,
    canceledBy: string,
    reason: string,
  ) {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.cancelReservation(target.id, canceledBy, reason);
      setReservations((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      setCancelTarget(null);
    } catch (err) {
      setError('Failed to cancel: ' + errorMessage(err));
    }
    setSaving(false);
  }

  async function assignReservation(target: Reservation, equipment: string) {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.assignReservation(target.id, equipment);
      setReservations((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      setAssignTarget(null);
    } catch (err) {
      setError('Failed to assign: ' + errorMessage(err));
    }
    setSaving(false);
  }

  async function updateReservation(
    target: Reservation,
    changes: Partial<ReservationEdit>,
  ) {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateReservation(target.id, changes);
      setReservations((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      setEditingReservation(null);
    } catch (err) {
      setError('Failed to save reservation: ' + errorMessage(err));
    }
    setSaving(false);
  }

  async function saveEquipment(input: EquipmentInput) {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await api.updateEquipment(editing.id, input);
      } else {
        const { isActive: _unused, ...create } = input;
        await api.createEquipment(create);
      }
      setCatalogueOpen(false);
      setEditing(null);
      setSelected(null);
      // A rename cascades into reservations, and a retire drops the row
      // out of the default list — reload both sides rather than trying
      // to patch local state.
      await loadData();
    } catch (err) {
      setError('Failed to save resource: ' + errorMessage(err));
    }
    setSaving(false);
  }

  // ── Derived state ───────────────────────────────────────────
  const availabilityOf = useCallback(
    (title: string): Availability => {
      const current = reservations.find(
        (r) =>
          r.equipment === title &&
          r.startDate <= today &&
          r.endDate >= today &&
          r.status !== 'Canceled',
      );
      if (current) return current.status === 'Active' ? 'In Use' : 'Reserved';
      const upcoming = reservations.find(
        (r) =>
          r.equipment === title && r.startDate > today && r.status !== 'Canceled',
      );
      return upcoming ? 'Reserved' : 'Available';
    },
    [reservations, today],
  );

  const nextReservationOf = useCallback(
    (title: string): Reservation | null =>
      reservations
        .filter(
          (r) =>
            r.equipment === title &&
            r.startDate >= today &&
            r.status !== 'Canceled',
        )
        .sort((a, b) => a.startDate.localeCompare(b.startDate))[0] ?? null,
    [reservations, today],
  );

  const enriched: EnrichedResource[] = useMemo(
    () =>
      resources.map((r) => ({
        ...r,
        cal: calibrationStatus(r.nextDue, r.calStatus, today),
        avail: availabilityOf(r.title),
      })),
    [resources, availabilityOf, today],
  );

  // Only in-service resources can be booked. Matters once an admin
  // turns on "include retired" — those rows are visible but must not
  // reach the reservation picker.
  const bookable = useMemo(
    () => enriched.filter((r) => r.isActive && r.status === 'Available'),
    [enriched],
  );

  // The status vocabulary came from a SharePoint choice column, so the
  // edit form offers what's actually in use rather than a guess.
  const statusOptions = useMemo(
    () => [...new Set(resources.map((r) => r.status).filter(Boolean))],
    [resources],
  );

  const categories = useMemo(
    () => [
      'All',
      ...[
        ...new Set(
          enriched
            .filter((r) => r.type === 'Equipment')
            .map((r) => r.category)
            .filter(Boolean),
        ),
      ].sort(),
    ],
    [enriched],
  );

  const offices = useMemo(
    () => [
      'All Offices',
      ...[...new Set(enriched.map((r) => r.office).filter(Boolean))].sort(),
    ],
    [enriched],
  );

  const filtered = useMemo(
    () =>
      enriched.filter((r) => {
        const term = search.toLowerCase();
        return (
          (!term ||
            r.name.toLowerCase().includes(term) ||
            r.title.toLowerCase().includes(term) ||
            r.category.toLowerCase().includes(term)) &&
          (typeFilter === 'All Resources' || r.type === typeFilter) &&
          (categoryFilter === 'All' ||
            r.category === categoryFilter ||
            r.type === 'Personnel') &&
          (officeFilter === 'All Offices' ||
            r.office === officeFilter ||
            r.type === 'Personnel')
        );
      }),
    [enriched, search, typeFilter, categoryFilter, officeFilter],
  );

  const filteredReservations = useMemo(
    () =>
      reservations.filter((r) => {
        const term = reservationSearch.toLowerCase();
        return (
          (!term ||
            r.projectNumber.toLowerCase().includes(term) ||
            r.projectName.toLowerCase().includes(term) ||
            r.reservedBy.toLowerCase().includes(term) ||
            r.equipment.toLowerCase().includes(term)) &&
          (reservationStatusFilter === 'All' ||
            r.status === reservationStatusFilter) &&
          (!unassignedOnly || r.equipment === '')
        );
      }),
    [reservations, reservationSearch, reservationStatusFilter, unassignedOnly],
  );

  const unassignedCount = useMemo(
    () => reservations.filter((r) => r.equipment === '' && r.status !== 'Canceled').length,
    [reservations],
  );

  const counts = {
    equipment: enriched.filter((r) => r.type === 'Equipment').length,
    personnel: enriched.filter((r) => r.type === 'Personnel').length,
    available: enriched.filter((r) => r.avail === 'Available').length,
    assigned: enriched.filter(
      (r) => r.avail === 'Reserved' || r.avail === 'In Use',
    ).length,
    calAlerts: enriched.filter((r) => r.cal && r.cal !== 'Current').length,
  };

  // Four weeks starting from the Sunday of the current week.
  const matrixDays = useMemo(() => {
    const sunday = addDays(today, -parseIsoDate(today).getDay());
    return Array.from({ length: 28 }, (_, i) => addDays(sunday, i));
  }, [today]);

  const isReservedOn = useCallback(
    (title: string, date: string) =>
      reservations.some(
        (r) =>
          r.equipment === title &&
          r.startDate <= date &&
          r.endDate >= date &&
          r.status !== 'Canceled',
      ),
    [reservations],
  );

  const matrixRows =
    typeFilter === 'Personnel'
      ? enriched.filter((r) => r.type === 'Personnel')
      : typeFilter === 'Equipment'
        ? enriched.filter((r) => r.type === 'Equipment')
        : enriched;

  const displayName = user?.displayName || user?.email || '';

  // ── Render states ───────────────────────────────────────────
  if (authState === 'loading') {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner" />
          <span>Initializing…</span>
        </div>
      </div>
    );
  }

  if (authState === 'loggedout') {
    return (
      <div className="app">
        <div className="login-screen">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: 'var(--blue-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
            }}
          >
            📋
          </div>
          <h1>Resource Manager</h1>
          <p>
            Exact Metrology · Sign in with your Microsoft 365 account to access
            equipment and personnel resources.
          </p>
          {error && <div className="error-banner">{error}</div>}
          <button className="login-btn" onClick={login}>
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  if (dataLoading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner" />
          <span>Loading resources…</span>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────
  return (
    <div className="app">
      {cancelTarget && (
        <CancelModal
          target={cancelTarget}
          saving={saving}
          onConfirm={cancelReservation}
          onClose={() => setCancelTarget(null)}
        />
      )}
      {selected && (
        <ResourceModal
          resource={selected}
          reservations={reservations}
          defaultReservedBy={displayName}
          saving={saving}
          onAdd={addReservation}
          canSchedule={user?.isScheduler === true}
          onCancel={(r) => setCancelTarget(r)}
          onEditReservation={(r) => setEditingReservation(r)}
          onEdit={
            user?.isAdmin
              ? () => {
                  setEditing(selected);
                  setCatalogueOpen(true);
                }
              : undefined
          }
          onClose={() => setSelected(null)}
        />
      )}
      {assignTarget && (
        <AssignModal
          reservation={assignTarget}
          resources={bookable}
          reservations={reservations}
          saving={saving}
          onAssign={assignReservation}
          onClose={() => setAssignTarget(null)}
        />
      )}
      {editingReservation && (
        <EditReservationModal
          target={editingReservation}
          saving={saving}
          onConfirm={updateReservation}
          onClose={() => setEditingReservation(null)}
        />
      )}
      {catalogueOpen && (
        <EquipmentModal
          resource={editing}
          statusOptions={statusOptions}
          bookingCount={
            editing
              ? reservations.filter((r) => r.equipment === editing.title).length
              : 0
          }
          saving={saving}
          onSave={saveEquipment}
          onClose={() => {
            setCatalogueOpen(false);
            setEditing(null);
          }}
        />
      )}

      <div className="header">
        <div>
          <h1>Resource Manager</h1>
          <p>Exact Metrology · Live Snowflake data · {today}</p>
        </div>
        <div className="user-info">
          <div className="user-avatar">{initials(displayName)}</div>
          <span>{displayName}</span>
          <button className="signout-btn" onClick={logout}>
            Sign out
          </button>
          <button className="signout-btn" onClick={() => void loadData()}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {user?.adminCheck &&
        !user.adminCheck.membershipChecked &&
        user.adminCheck.hint && (
          <div
            className="error-banner"
            style={{
              background: 'var(--amber-bg)',
              borderColor: 'var(--amber-bd)',
              color: 'var(--amber-fg)',
            }}
          >
            <strong>Admin check could not complete.</strong>{' '}
            {user.adminCheck.hint}
            {user.adminCheck.error && (
              <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
                {user.adminCheck.error}
              </div>
            )}
          </div>
        )}

      {error && (
        <div className="error-banner">
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 8,
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 6,
              background: 'transparent',
              border: '0.5px solid var(--red-bd)',
              color: 'var(--red-fg)',
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="kpis">
        {(
          [
            ['Equipment', counts.equipment, '#e6f1fb'],
            ['Personnel', counts.personnel, '#eeedfe'],
            ['Available', counts.available, '#eaf3de'],
            ['Assigned', counts.assigned, '#faeeda'],
            ['Cal. Alerts', counts.calAlerts, '#fcebeb'],
          ] as [string, number, string][]
        ).map(([label, value, background]) => (
          <div key={label} className="kpi" style={{ background }}>
            <div className="label">{label}</div>
            <div className="value">{value}</div>
          </div>
        ))}
      </div>

      {showNew && (
        <NewReservationForm
          resources={bookable}
          reservations={reservations}
          defaultReservedBy={displayName}
          saving={saving}
          onSubmit={addReservation}
          onClose={() => setShowNew(false)}
        />
      )}

      <div className="toolbar">
        <div style={{ display: 'flex', gap: 4 }}>
          {[...VIEWS, ...(user?.isAdmin ? ADMIN_VIEWS : [])].map(([id, label]) => (
            <button
              key={id}
              className={`tab-btn${view === id ? ' active' : ''}`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        {user?.isScheduler && (
          <button className="new-res-btn" onClick={() => setShowNew((v) => !v)}>
            + New reservation
          </button>
        )}
        {user?.isAdmin && (
          <button
            className="new-res-btn"
            style={{
              background: 'var(--blue-bg)',
              color: 'var(--blue-fg)',
              borderColor: 'var(--blue-bd)',
            }}
            onClick={() => {
              setEditing(null);
              setCatalogueOpen(true);
            }}
          >
            + Add resource
          </button>
        )}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          style={{ minWidth: 130 }}
        />
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setCategoryFilter('All');
            setOfficeFilter('All Offices');
          }}
        >
          {['All Resources', 'Equipment', 'Personnel'].map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
        {(typeFilter === 'Equipment' || typeFilter === 'All Resources') && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        )}
        {typeFilter === 'Equipment' && (
          <select
            value={officeFilter}
            onChange={(e) => setOfficeFilter(e.target.value)}
          >
            {offices.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        )}
        {user?.isAdmin && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            <input
              type="checkbox"
              style={{ width: 'auto', padding: 0 }}
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
            />
            Include retired
          </label>
        )}
      </div>

      {view === 'resources' && (
        <div className="table">
          <div className="table-head res-grid">
            <span>Name</span>
            <span>Type</span>
            <span>Role / Category</span>
            <span>Availability</span>
            <span>Calibration</span>
            <span>Next reservation</span>
          </div>
          {filtered.map((r) => {
            const next = nextReservationOf(r.title);
            return (
              <div
                key={r.id}
                className={`table-row res-grid${
                  r.avail === 'Available' ? '' : ' row-reserved'
                }`}
                onClick={() => setSelected(r)}
              >
                <div className="name-cell">
                  {r.type === 'Personnel' && (
                    <div className="avatar">{initials(r.name)}</div>
                  )}
                  <div>
                    <div className="name-primary">{r.name}</div>
                    {r.type === 'Equipment' && (
                      <div className="name-sub">{r.title}</div>
                    )}
                  </div>
                </div>
                <span>
                  <Badge
                    label={r.type === 'Personnel' ? 'Person' : 'Equip.'}
                    colors={{
                      Person: TYPE_COLORS.Personnel,
                      'Equip.': TYPE_COLORS.Equipment,
                    }}
                    fontSize={10}
                  />
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {r.category}
                  {r.office ? ` · ${r.office}` : ''}
                </span>
                <span>
                  {/* Retired and out-of-service rows only appear with
                      "include retired" on. Availability is meaningless
                      for them, so say why they're here instead. */}
                  {!r.isActive ? (
                    <Badge label="Retired" colors={{}} />
                  ) : r.status !== 'Available' ? (
                    <Badge label={r.status} colors={{}} />
                  ) : (
                    <Badge label={r.avail} colors={AVAILABILITY_COLORS} />
                  )}
                </span>
                <span>
                  {r.cal ? (
                    <Badge label={r.cal} colors={CAL_COLORS} />
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: next ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                  }}
                >
                  {next ? `${next.projectNumber} · ${next.startDate}` : '—'}
                </span>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="empty">No resources match filters.</div>
          )}
        </div>
      )}

      {view === 'matrix' && (
        <div className="matrix-wrap">
          <div className="matrix">
            <div className="matrix-head">
              <span style={{ fontWeight: 500 }}>Resource</span>
              {matrixDays.map((d) => (
                <span key={d} style={{ textAlign: 'center' }}>
                  {d.slice(5)}
                </span>
              ))}
            </div>
            {matrixRows.map((r) => (
              <div
                key={r.id}
                className="matrix-row"
                onClick={() => setSelected(r)}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color:
                      r.type === 'Personnel' ? '#534ab7' : 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.name}
                </span>
                {matrixDays.map((d) => (
                  <div
                    key={d}
                    className="matrix-cell"
                    style={{
                      background: isReservedOn(r.title, d)
                        ? r.type === 'Personnel'
                          ? '#ede9fe'
                          : '#faece7'
                        : '#eaf3de',
                      border: d === today ? '1.5px solid #378add' : 'none',
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="matrix-legend">
            <span>
              <span className="legend-dot" style={{ background: '#eaf3de' }} />
              Available
            </span>
            <span>
              <span className="legend-dot" style={{ background: '#faece7' }} />
              Equipment reserved
            </span>
            <span>
              <span className="legend-dot" style={{ background: '#ede9fe' }} />
              Personnel assigned
            </span>
            <span>
              <span
                className="legend-dot"
                style={{ border: '1.5px solid #378add' }}
              />
              Today
            </span>
          </div>
        </div>
      )}

      {view === 'audit' && user?.isAdmin && (
        <AuditLog onSignedOut={() => setAuthState('loggedout')} />
      )}

      {view === 'reports' && (
        <CalibrationReport
          today={today}
          onSignedOut={() => setAuthState('loggedout')}
        />
      )}

      {view === 'reservations' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              value={reservationSearch}
              onChange={(e) => setReservationSearch(e.target.value)}
              placeholder="Search reservations…"
              style={{ minWidth: 160 }}
            />
            <select
              value={reservationStatusFilter}
              onChange={(e) => setReservationStatusFilter(e.target.value)}
            >
              {['All', 'Active', 'Confirmed', 'Pending', 'Canceled'].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              <input
                type="checkbox"
                style={{ width: 'auto', padding: 0 }}
                checked={unassignedOnly}
                onChange={(e) => setUnassignedOnly(e.target.checked)}
              />
              Unassigned only
              {unassignedCount > 0 && (
                <span
                  className="badge"
                  style={{
                    background: 'var(--amber-bg)',
                    color: 'var(--amber-fg)',
                    borderColor: 'var(--amber-bd)',
                  }}
                >
                  {unassignedCount}
                </span>
              )}
            </label>
          </div>
          <div className="table">
            <div
              className="table-head"
              style={{ gridTemplateColumns: '1.4fr 1.8fr 1.2fr 1fr 1fr 80px' }}
            >
              <span>Project</span>
              <span>Resource</span>
              <span>Reserved by</span>
              <span>Dates</span>
              <span>Status</span>
              <span />
            </div>
            {filteredReservations.map((r) => {
              const resource = enriched.find((e) => e.title === r.equipment);
              const isPerson = resource?.type === 'Personnel';
              return (
                <div
                  key={r.id}
                  className="table-row"
                  style={{
                    gridTemplateColumns: '1.4fr 1.8fr 1.2fr 1fr 1fr 80px',
                  }}
                  onClick={() => resource && setSelected(resource)}
                >
                  <div>
                    <div className="name-primary">{r.projectNumber}</div>
                    <div className="name-sub">{r.projectName}</div>
                  </div>
                  <div className="name-cell">
                    {isPerson && (
                      <div
                        className="avatar"
                        style={{ width: 24, height: 24, fontSize: 9 }}
                      >
                        {resource ? initials(resource.name) : '?'}
                      </div>
                    )}
                    <div>
                      {r.equipment === '' ? (
                        // Imported without a resource, or deliberately
                        // unassigned. Offer the fix inline rather than
                        // rendering a blank cell.
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignTarget(r);
                          }}
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            padding: '3px 9px',
                            borderRadius: 6,
                            background: 'var(--amber-bg)',
                            color: 'var(--amber-fg)',
                            border: '0.5px solid var(--amber-bd)',
                          }}
                        >
                          Assign resource
                        </button>
                      ) : (
                        <>
                          <div
                            style={{ fontSize: 13, color: 'var(--text-primary)' }}
                          >
                            {resource?.name || r.equipment}
                          </div>
                          {!isPerson && (
                            <div className="name-sub">{r.equipment}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {r.reservedBy}
                  </span>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    <div>{r.startDate}</div>
                    <div>{r.endDate}</div>
                  </div>
                  <span>
                    <Badge label={r.status} colors={RESERVATION_COLORS} />
                  </span>
                  <span
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    {/* Cancel goes last, and set apart. It was the top
                        button in this stack — the first thing under the
                        cursor — and it's the one action here that ends a
                        booking. */}
                    {r.status !== 'Canceled' && user?.isScheduler && (
                      <>
                        <button
                          onClick={() => setEditingReservation(r)}
                          style={{
                            fontSize: 10,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)',
                            border: '0.5px solid var(--border-md)',
                          }}
                        >
                          Edit
                        </button>
                        {r.equipment !== '' && (
                          <button
                            onClick={() => setAssignTarget(r)}
                            style={{
                              fontSize: 10,
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-secondary)',
                              border: '0.5px solid var(--border-md)',
                            }}
                          >
                            Reassign
                          </button>
                        )}
                        <button
                          onClick={() => setCancelTarget(r)}
                          style={{
                            fontSize: 11,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: 'var(--red-bg)',
                            color: 'var(--red-fg)',
                            border: '0.5px solid var(--red-bd)',
                            marginTop: 4,
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
            {filteredReservations.length === 0 && (
              <div className="empty">No reservations match filters.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
